class ProductCustomizer {
  constructor() {
    this.customizedImages = [];
    this.storageKey = 'customized_images';
    this.lastCaptureTime = 0;
    this.captureDebounceTime = 500;
    this.init();
  }

  init() {
    this.loadSavedImages();
    this.setupCustomizerObserver();
    this.setupMutationObserver();
    this.attachCartObserver();
    
    // Use requestAnimationFrame to ensure DOM is fully painted before displaying
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        requestAnimationFrame(() => this.displayCustomizedImages());
      });
    } else {
      requestAnimationFrame(() => this.displayCustomizedImages());
    }
    
    this.renderCartCustomizations();
    this.observeCartDomMutations();
  }

  loadSavedImages() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      this.customizedImages = saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading saved images:', error);
      this.customizedImages = [];
    }
  }

  setupCustomizerObserver() {
    document.addEventListener('customizer:updated', () => this.captureImageWithDebounce());
    document.addEventListener('customizer:saved', () => this.captureImageWithDebounce());
  }

  setupMutationObserver() {
    const customizer = document.querySelector('[data-customizer]');
    if (!customizer) return;

    const observer = new MutationObserver(() => this.captureImageWithDebounce());
    observer.observe(customizer, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'data', 'style'] });

    const canvasElements = customizer.querySelectorAll('canvas');
    if (canvasElements.length > 0) {
      canvasElements.forEach(canvas => {
        try {
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          const originalFillRect = ctx.fillRect;
          const self = this;
          ctx.fillRect = function(...args) {
            originalFillRect.apply(this, args);
            self.captureImageWithDebounce();
          };
        } catch (e) { /* ignore */ }
      });
    }
  }

  captureImageWithDebounce() {
    const now = Date.now();
    if (now - this.lastCaptureTime < this.captureDebounceTime) return;
    this.lastCaptureTime = now;
    this.captureCustomizationImage();
  }

  captureCustomizationImage() {
    const customizer = document.querySelector('[data-customizer]');
    if (!customizer) return;

    try {
      const canvas = customizer.querySelector('canvas');
      let imageData = null;

      if (canvas) {
        imageData = canvas.toDataURL('image/png');
      } else {
        const img = customizer.querySelector('img[data-customized], img[src*="customiz"], img.pci-canvas-overlay-img');
        if (img && img.src) imageData = img.src;
      }

      if (imageData) this.addImageIfNew(imageData);
    } catch (error) {
      console.error('Error capturing customization image:', error);
    }
  }

  addImageIfNew(imageData) {
    const exists = this.customizedImages.some(img => {
      if (!img || !imageData) return false;
      if (img === imageData) return true;
      return img.substring(0, 100) === imageData.substring(0, 100);
    });

    if (!exists && imageData) {
      this.customizedImages.push(imageData);
      this.saveImages();
      this.displayCustomizedImages();
      this.updateCartAttributes();
      this.renderCartCustomizations();
    }
  }

  displayCustomizedImages() {
    const containers = document.querySelectorAll('.customized-images__container');
    
    if (containers.length === 0) {
      console.warn('No customized-images__container found in DOM');
      return;
    }

    containers.forEach(container => {
      const wrapper = container.closest('.customized-images-wrapper');
      container.innerHTML = '';

      if (this.customizedImages.length === 0) {
        if (wrapper) wrapper.style.display = 'none';
        return;
      }

      this.customizedImages.forEach((imageData, index) => {
        const imageWrapper = document.createElement('div');
        imageWrapper.className = 'customized-images__item';

        const img = document.createElement('img');
        img.src = imageData;
        img.alt = `Customization ${index + 1}`;
        img.className = 'customized-images__image';
        img.loading = 'lazy';

        const removeBtn = document.createElement('button');
        removeBtn.className = 'customized-images__remove focus-inset';
        removeBtn.type = 'button';
        removeBtn.innerHTML = '&times;';
        removeBtn.setAttribute('aria-label', 'Remove customization');

        removeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const idx = this.customizedImages.indexOf(imageData);
          if (idx > -1) this.customizedImages.splice(idx, 1);
          this.saveImages();
          this.displayCustomizedImages();
          this.updateCartAttributes();
          this.renderCartCustomizations();
        });

        imageWrapper.appendChild(img);
        imageWrapper.appendChild(removeBtn);
        container.appendChild(imageWrapper);
      });

      if (wrapper) {
        wrapper.style.display = 'block';
      }
    });
  }

  saveImages() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.customizedImages));
    } catch (error) {
      console.error('Error saving images:', error);
    }
  }

  updateCartAttributes() {
    const customizedImagesJson = JSON.stringify(this.customizedImages || []);
    const productForm = document.querySelector('[data-type="add-to-cart-form"]');
    
    if (productForm) {
      // PRIMARY: Store user-friendly property name for Shopify order display
      // This will show in order admin line item properties
      let propInput = productForm.querySelector('input[name="properties[Customized Images]"]');
      if (!propInput) {
        propInput = document.createElement('input');
        propInput.type = 'hidden';
        propInput.name = 'properties[Customized Images]';
        productForm.appendChild(propInput);
      }
      // Store as JSON array - Shopify will display this in order line items
      propInput.value = customizedImagesJson;

      // Count property for display
      let propCount = productForm.querySelector('input[name="properties[Custom Image Count]"]');
      if (!propCount) {
        propCount = document.createElement('input');
        propCount.type = 'hidden';
        propCount.name = 'properties[Custom Image Count]';
        productForm.appendChild(propCount);
      }
      propCount.value = String(this.customizedImages.length);

      // SECONDARY: Store for internal cart operations
      let internalData = productForm.querySelector('input[name="properties[_Custom Image Data]"]');
      if (!internalData) {
        internalData = document.createElement('input');
        internalData.type = 'hidden';
        internalData.name = 'properties[_Custom Image Data]';
        productForm.appendChild(internalData);
      }
      internalData.value = customizedImagesJson;

      // TERTIARY: Store image count for easy reference in order admin
      let imageCountDisplay = productForm.querySelector('input[name="properties[Images Applied]"]');
      if (!imageCountDisplay) {
        imageCountDisplay = document.createElement('input');
        imageCountDisplay.type = 'hidden';
        imageCountDisplay.name = 'properties[Images Applied]';
        productForm.appendChild(imageCountDisplay);
      }
      imageCountDisplay.value = `${this.customizedImages.length} customization(s)`;
    }

    // Also update cart-level attributes for compatibility
    const cartForm = document.querySelector('form[action^="/cart"]') || productForm;
    if (cartForm) {
      let attrInput = cartForm.querySelector('input[name="attributes[Customized Images]"]');
      if (!attrInput) {
        attrInput = document.createElement('input');
        attrInput.type = 'hidden';
        attrInput.name = 'attributes[Customized Images]';
        cartForm.appendChild(attrInput);
      }
      attrInput.value = customizedImagesJson;
    }
  }

  attachCartObserver() {
    document.addEventListener('cart:updated', () => {
      this.ensureCartHasCustomizations();
      this.renderCartCustomizations();
    });

    document.addEventListener('cart:drawer:open', () => this.renderCartCustomizations());
    document.addEventListener('cart:refresh', () => this.renderCartCustomizations());

    const productForm = document.querySelector('[data-type="add-to-cart-form"]');
    if (productForm) {
      productForm.addEventListener('submit', (e) => {
        // Ensure properties are set before form submission
        this.updateCartAttributes();
        console.log('Cart attributes updated before submission');
      });
    }
  }

  ensureCartHasCustomizations() {
    if (this.customizedImages.length > 0) this.updateCartAttributes();
  }

  renderCartCustomizations(retryCount = 0) {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        if (!cart || !cart.items) return;
        cart.items.forEach(item => {
          const props = item.properties || {};
          if (!props || !props['_Custom Image Data']) return;

          let images;
          try {
            images = JSON.parse(props['_Custom Image Data']);
          } catch (e) {
            images = null;
          }
          if (!images || images.length === 0) return;

          const cartItemEl = this.findCartItemElement(item.key);
          if (!cartItemEl) {
            if (retryCount < 5) {
              setTimeout(() => this.renderCartCustomizations(retryCount + 1), 300);
            }
            return;
          }

          this.injectCustomizationsIntoCartItem(cartItemEl, images);
        });
      })
      .catch(err => {
        // ignore fetch failures
      });
  }

  findCartItemElement(itemKey) {
    const selectors = [
      `[data-cart-item-key="${itemKey}"]`,
      `[data-line-item-key="${itemKey}"]`,
      `#CartItem-${itemKey}`,
      `.cart-item[data-key="${itemKey}"]`,
      `.cart__row[data-line-item-key="${itemKey}"]`,
      `.cart-item[data-line-item-key="${itemKey}"]`,
      `.drawer__inner [data-line-item-key="${itemKey}"]`,
      `.cart-drawer [data-line-item-key="${itemKey}"]`,
      `.ajaxcart__product[data-key="${itemKey}"]`,
      `.cart__product[data-key="${itemKey}"]`
    ];

    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el;
    }

    const possibleRows = document.querySelectorAll('.cart-item, .cart__row, .cart__product, .cart-item__row');
    for (const row of possibleRows) {
      if (row.innerText && row.innerText.indexOf(itemKey) !== -1) {
        return row;
      }
    }

    return null;
  }

  injectCustomizationsIntoCartItem(cartItemEl, images) {
    if (!cartItemEl) return;

    let customizationDiv = cartItemEl.querySelector('.cart-item__customizations');
    if (!customizationDiv) {
      customizationDiv = document.createElement('div');
      customizationDiv.className = 'cart-item__customizations';
      const titleSelectors = [
        '.cart-item__title',
        '.cart__product-title',
        '.cart-item__name',
        '.cart-item__info',
        '.cart-product__title',
        '.cart__meta-title',
        '.product-title'
      ];
      let inserted = false;
      for (const tSel of titleSelectors) {
        const titleEl = cartItemEl.querySelector(tSel);
        if (titleEl) {
          titleEl.parentNode.insertBefore(customizationDiv, titleEl.nextSibling);
          inserted = true;
          break;
        }
      }
      if (!inserted) {
        cartItemEl.appendChild(customizationDiv);
      }
    }

    customizationDiv.innerHTML = '';
    const title = document.createElement('div');
    title.className = 'cart-item__customizations-title';
    title.textContent = 'Customizations:';
    customizationDiv.appendChild(title);

    const thumbs = document.createElement('div');
    thumbs.className = 'cart-item__customizations-images';
    images.forEach(src => {
      const img = document.createElement('img');
      img.src = src;
      img.alt = 'Customization';
      img.className = 'cart-item__customization-image';
      img.loading = 'lazy';
      thumbs.appendChild(img);
    });
    customizationDiv.appendChild(thumbs);
  }

  observeCartDomMutations() {
    if (this._cartObserver) return;
    const observer = new MutationObserver((mutations) => {
      let shouldRender = false;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          const nodeHtml = node.className || '';
          if (nodeHtml.indexOf('cart') !== -1 || nodeHtml.indexOf('drawer') !== -1 || (node.querySelector && node.querySelector('[data-line-item-key]'))) {
            shouldRender = true;
            break;
          }
        }
        if (shouldRender) break;
      }
      if (shouldRender) {
        if (this._cartRenderTimeout) clearTimeout(this._cartRenderTimeout);
        this._cartRenderTimeout = setTimeout(() => this.renderCartCustomizations(), 250);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    this._cartObserver = observer;
  }

  clearCustomizations() {
    this.customizedImages = [];
    this.saveImages();
    this.displayCustomizedImages();
    const wrapper = document.querySelector('.customized-images-wrapper');
    if (wrapper) wrapper.style.display = 'none';
    this.updateCartAttributes();
    this.renderCartCustomizations();
  }

  addCustomizationImage(imageData) {
    if (imageData) {
      this.addImageIfNew(imageData);
    }
  }

  static getInstance() {
    if (!window.productCustomizerInstance) {
      window.productCustomizerInstance = new ProductCustomizer();
    }
    return window.productCustomizerInstance;
  }
}

// Initialize when DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productCustomizer = ProductCustomizer.getInstance();
    // Ensure display is called after instance is ready
    setTimeout(() => {
      if (window.productCustomizer) {
        window.productCustomizer.displayCustomizedImages();
      }
    }, 100);
  });
} else {
  window.productCustomizer = ProductCustomizer.getInstance();
  // Call immediately if already loaded
  setTimeout(() => {
    if (window.productCustomizer) {
      window.productCustomizer.displayCustomizedImages();
    }
  }, 100);
}
